import React from 'react';
import { useRouter } from 'next/router';
import { defineMessages, useIntl } from 'react-intl';

import { QUERY_PARAMS } from '@/src/listings/common/constants';

import { VERTICAL } from '@/src/listings/types';
import { SignalInfo } from '@/src/listings/components/_shared/Icons';

import styles from './SalesEndedMessage.module.scss';

interface SalesEndedMessageProps {
  body?: string;
  vertical?: string;
}

const MESSAGES = defineMessages({
  registrationSalesEndedHeader: {
    id: 'Registrations are closed',
    defaultMessage: 'Registrations are closed',
  },
  ticketSalesEndedHeader: {
    id: 'Ticket sales have ended',
    defaultMessage: 'Ticket sales have ended',
  },
});

const hasQueryParamValue = (value: string | string[] | undefined): boolean => {
  if (Array.isArray(value)) {
    return value.some((entry) => entry?.trim().length > 0);
  }

  return (value?.trim().length ?? 0) > 0;
};

export const SalesEndedMessage: React.FC<SalesEndedMessageProps> = ({ body, vertical }) => {
  const intl = useIntl();
  const { query } = useRouter();

  // Keep parity with both known waitlist query keys used by listing flows.
  const hasReleasedWaitlistCode =
    hasQueryParamValue(query.waitlist_code) || hasQueryParamValue(query[QUERY_PARAMS.WAITLIST_TOKEN]);
  const salesEndedMessageBody = typeof body === 'string' && body.trim().length > 0 ? body : undefined;

  if (!salesEndedMessageBody || hasReleasedWaitlistCode) {
    return null;
  }

  const header =
    vertical === VERTICAL.REGISTRATION
      ? intl.formatMessage(MESSAGES.registrationSalesEndedHeader)
      : intl.formatMessage(MESSAGES.ticketSalesEndedHeader);

  return (
    <section className={styles.salesEndedMessage} data-testid="sales-ended-message">
      <div className={styles.salesEndedMessageHeaderRow}>
        <span className={styles.salesEndedMessageIcon} aria-hidden="true">
          <SignalInfo />
        </span>
        <h2 className={styles.salesEndedMessageHeader}>{header}</h2>
      </div>
      <p className={styles.salesEndedMessageBody}>{salesEndedMessageBody}</p>
    </section>
  );
};
